/**
 * Project class with encapsulated business logic
 */

import {
  Month,
  MonthlyEffort,
  WorkCategory,
  EffortBreakdown,
  Status,
  fromStringStatus,
  ProjectTask,
  TaskStatus,
} from "../types";

/**
 * Project class representing a single project in the schedule
 * Encapsulates all project-specific calculations and maintains monthly effort cache
 */
export class Project {
  id: number | null;
  readonly name: string;
  startDate?: Date;
  endDate?: Date;
  readonly effort: number;
  readonly color: string;
  readonly effortBreakdown: EffortBreakdown;
  readonly tasks: ProjectTask[];
  readonly clientProjectIds: string[];
  private _status: Status;

  get status(): Status {
    return this._status;
  }

  set status(value: Status) {
    this._status = value;
  }

  constructor(data: {
    id: number | null;
    name: string;
    startDate?: Date;
    endDate?: Date;
    color: string;
    effortBreakdown?: EffortBreakdown;
    tasks?: ProjectTask[];
    clientProjectIds?: string[];
  }) {
    this.id = data.id;
    this.name = data.name;
    this.startDate = data.startDate;
    this.endDate = data.endDate;
    this.color = data.color;
    this._status = Status.BACKLOG;

    this.effortBreakdown = data.effortBreakdown || {
      [WorkCategory.DEVELOPMENT]: 0,
      [WorkCategory.IA]: 0,
      [WorkCategory.PROJECT_MANAGEMENT]: 0,
      [WorkCategory.CONTENT_CREATION]: 0,
      [WorkCategory.COG_SCI]: 0,
    };
    this.tasks = data.tasks || [];
    this.clientProjectIds = data.clientProjectIds || [];

    // Calculate total effort from breakdown, or use provided effort
    this.effort = this.calculateTotalEffort();
  }

  static build(p: any) {
    let project = new Project({
      ...p,
      tasks: (p.tasks || []).map((t: any) => ({
        id: t.id,
        name: t.name,
        weekStart: t.weekStart,
        weekCount: Number(t.weekCount) > 0 ? Number(t.weekCount) : 1,
        status: t.status || TaskStatus.TODO,
      })),
      clientProjectIds: Array.isArray(p.clientProjectIds)
        ? p.clientProjectIds.filter((id: unknown) => typeof id === "string")
        : [],
    });

    project.startDate = p.startDate ? new Date(p.startDate) : undefined;
    project.endDate = p.endDate ? new Date(p.endDate) : undefined;
    console.log("Status ", p.status);
    project.status = fromStringStatus(p.status);
    console.log("Parsed Status", project.status);

    return project;
  }

  /**
   * Get the total duration of the project in days
   * @returns Duration in days
   */
  private getDurationInDays(): number | undefined {
    if (!this.endDate || !this.startDate) return undefined;
    return (
      (this.endDate.getTime() - this.startDate.getTime()) /
      (1000 * 60 * 60 * 24)
    );
  }

  /**
   * Calculate how many days this project overlaps with a given month
   * @param month - Month to check overlap with
   * @returns Number of overlapping days (0 if no overlap)
   */
  private getOverlapDaysWithMonth(month: Month): number {
    if (!this.startDate || !this.endDate) return 0;
    const monthStart = new Date(month.year, month.month, 1);
    const monthEnd = new Date(month.year, month.month + 1, 0);

    // Check if project overlaps with this month
    if (this.startDate <= monthEnd && this.endDate >= monthStart) {
      const overlapStart =
        this.startDate > monthStart ? this.startDate : monthStart;
      const overlapEnd = this.endDate < monthEnd ? this.endDate : monthEnd;
      return (
        (overlapEnd.getTime() - overlapStart.getTime()) /
          (1000 * 60 * 60 * 24) +
        1
      );
    }

    return 0;
  }

  /**
   * Get the effort for a specific work category
   * @param category - Work category to get effort for (optional, returns total if not specified)
   * @returns Effort in jours-homme for the specified category
   */
  getEffortForCategory(category?: WorkCategory | null): number {
    if (!category) {
      return this.effort; // Return total effort if no category specified
    }
    return this.effortBreakdown[category] || 0;
  }

  /**
   * Calculate the effort allocated to a specific month for a specific work category
   * @param month - Month to calculate effort for
   * @param category - Work category to filter by (optional, returns total if not specified)
   * @returns Effort for this month and category (jours-homme)
   */
  getEffortForMonth(month: Month, category?: WorkCategory | null): number {
    const overlapDays = this.getOverlapDaysWithMonth(month);
    const totalDays = this.getDurationInDays();
    if (!totalDays || overlapDays === 0) {
      return 0;
    }

    const effort = this.getEffortForCategory(category);

    const effortPerDay = effort / totalDays;

    return effortPerDay * overlapDays;
  }

  /**
   * Calculate and cache the effort distribution across all provided months
   * Uses caching to avoid redundant calculations
   * @param months - Array of months
   * @param category - Work category to filter by (optional)
   * @returns Object mapping month index to effort value
   */
  getMonthlyEffortDistribution(
    months: Month[],
    category?: WorkCategory | null
  ): MonthlyEffort {
    const distribution: MonthlyEffort = {};

    months.forEach((month, index) => {
      distribution[index] = this.getEffortForMonth(month, category);
    });
    return distribution;
  }

  /**
   * Check if this project overlaps with a given month
   * @param month - Month to check
   * @returns True if project is active during this month
   */
  isActiveInMonth(month: Month): boolean {
    return this.getOverlapDaysWithMonth(month) > 0;
  }

  /**
   * Create a copy of this project with updated properties
   * @param updates - Properties to update
   * @returns New Project instance with updates applied
   */
  update(
    updates: Partial<{
      id: number | null;
      name: string;
      startDate: Date;
      endDate: Date;
      color: string;
      effortBreakdown: EffortBreakdown;
      tasks: ProjectTask[];
      clientProjectIds: string[];
    }>
  ): Project {
    const updated = new Project({
      id: updates.id !== undefined ? updates.id : this.id,
      name: updates.name !== undefined ? updates.name : this.name,
      startDate:
        updates.startDate !== undefined ? updates.startDate : this.startDate,
      endDate: updates.endDate !== undefined ? updates.endDate : this.endDate,
      color: updates.color !== undefined ? updates.color : this.color,
      effortBreakdown:
        updates.effortBreakdown !== undefined
          ? updates.effortBreakdown
          : this.effortBreakdown,
      tasks: updates.tasks !== undefined ? updates.tasks : this.tasks,
      clientProjectIds:
        updates.clientProjectIds !== undefined
          ? updates.clientProjectIds
          : this.clientProjectIds,
    });
    updated.status = this.status;
    return updated;
  }

  getWeeklyTaskCounts(): Record<string, number> {
    return this.tasks.reduce<Record<string, number>>((acc, task) => {
      acc[task.weekStart] = (acc[task.weekStart] || 0) + 1;
      return acc;
    }, {});
  }

  /**
   * Calculate the total effort from the effort breakdown
   * @returns Total effort across all work categories
   */
  calculateTotalEffort(): number {
    return Object.values(this.effortBreakdown).reduce(
      (total, effort) => total + effort,
      0
    );
  }
}
